/**
 * CR225 Addendum: Preview Fixture Selector
 * 
 * Allows selection of test fixtures for deterministic UI control testing.
 * Sits beside the data source selector.
 */

import { useMemo } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import {
  Database,
  Star,
  Ribbon,
  Clock,
  Calendar,
  AlertTriangle,
  Sparkles,
  FlaskConical,
} from "lucide-react";
import { 
  type PreviewFixtureId, 
  PREVIEW_FIXTURES,
} from "@/lib/deals/previewFixtures";

// ================================================================
// TYPES
// ================================================================

interface PreviewFixtureSelectorProps {
  value: PreviewFixtureId;
  onChange: (id: PreviewFixtureId) => void;
  disabled?: boolean;
  compact?: boolean;
}

// Icon mapping
const ICON_MAP: Record<string, React.ElementType> = {
  database: Database,
  star: Star,
  ribbon: Ribbon,
  clock: Clock,
  calendar: Calendar,
  "alert-triangle": AlertTriangle,
  sparkles: Sparkles,
};

// ================================================================
// COMPONENT
// ================================================================

export function PreviewFixtureSelector({
  value,
  onChange,
  disabled = false,
  compact = false,
}: PreviewFixtureSelectorProps) {
  const currentFixture = useMemo(
    () => PREVIEW_FIXTURES.find((f) => f.id === value) || PREVIEW_FIXTURES[0],
    [value]
  );

  const CurrentIcon = ICON_MAP[currentFixture.icon] || Database;

  return (
    <div className="flex items-center gap-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1.5">
              <FlaskConical className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Fixture:</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p className="text-xs max-w-[200px]">
              Select a test fixture for deterministic UI testing. 
              Fixtures bypass the data source and provide known payloads.
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Select 
        value={value} 
        onValueChange={(v) => onChange(v as PreviewFixtureId)}
        disabled={disabled}
      >
        <SelectTrigger className="h-7 w-[160px] text-xs">
          <SelectValue>
            <span className="flex items-center gap-2">
              <CurrentIcon className="h-3.5 w-3.5" />
              {compact ? currentFixture.label.split(" ")[0] : currentFixture.label}
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {PREVIEW_FIXTURES.map((fixture) => {
            const Icon = ICON_MAP[fixture.icon] || Database;
            const isRealData = fixture.id === "real_data";
            
            return (
              <SelectItem key={fixture.id} value={fixture.id} className="text-xs">
                <div className="flex items-center gap-2">
                  <Icon className="h-3.5 w-3.5" />
                  <span>{fixture.label}</span>
                  {!isRealData && (
                    <Badge variant="outline" className="text-[9px] h-4 px-1 ml-1">
                      Test
                    </Badge>
                  )}
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}

export default PreviewFixtureSelector;
